/**
 * Bhairav PRODUCTION gates — frontend mirror of
 * backend/src/services/strategies/bhairavGates.ts (keep the two in sync, plus
 * the 'PROD curated' variant in backtest/bhairavSim.ts).
 *
 * Curated, backtest-validated entry-filter ladder (study rounds 1-3):
 *   WEEKLY (>=10080m)  raw Bhairav (sample too thin to certify a gate)
 *   DAILY  (>=1440m)   AvgCloud state green/red + close above/below EMA50
 *   4H     (>=240m)    Daily AND Weekly Bhairav CLH state aligned with the side
 *   below 4H           NO ENTRIES — every tested configuration loses to costs
 *
 * Gates suppress ENTRIES only; rule exits always run, so a gate-blocked
 * reversal becomes a plain exit. All HTF reads are CAUSAL (last fully-CLOSED
 * daily/weekly bar as-of the TF bar's close); weekly bars are Monday-anchored
 * calendar weeks derived from the daily bars.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import { computeCloudSeries } from '../avgCloud';
import { transformCandles } from '../candleTransforms';

export const BHAIRAV_AUTOTRADE_TFS = [240, 1440, 10080];
export type BhairavGateProfile = 'WEEKLY' | 'DAILY' | 'H4' | 'BLOCKED';
export type BhairavAction = 'BUY' | 'SHORT' | 'SELL' | 'COVER';

const ENTRY_WARMUP = 60;
const DAY = 86400;
const WEEK = 7 * DAY;
const MON_ANCHOR = 4 * DAY; // 1970-01-05 was a Monday

export function bhairavGateProfile(tfMinutes: number): BhairavGateProfile {
  if (tfMinutes >= 10080) return 'WEEKLY';
  if (tfMinutes >= 1440) return 'DAILY';
  if (tfMinutes >= 240) return 'H4';
  return 'BLOCKED';
}

export function bhairavGateLabel(profile: BhairavGateProfile): string {
  switch (profile) {
    case 'DAILY': return 'AvgCloud + EMA50 trend gate';
    case 'H4': return 'Daily+Weekly Bhairav alignment';
    case 'WEEKLY': return 'no gate (weekly)';
    default: return 'entries disabled below 4H';
  }
}

export interface GatedBhairavStep {
  time: number;
  pos: 'LONG' | 'SHORT' | 'FLAT';
  signal: BhairavAction | null;
  reversed?: boolean;
  /** Raw-rule entry the production gate suppressed on this bar (if any). */
  blockedSignal?: BhairavAction | null;
}

export interface GatedBhairavResult {
  steps: GatedBhairavStep[];
  profile: BhairavGateProfile;
  gateLabel: string;
  htfOk: boolean;
}

function emaSeries(closes: number[], period: number): number[] {
  const out = new Array<number>(closes.length);
  const k = 2 / (period + 1);
  let prev = 0, runSum = 0;
  for (let i = 0; i < closes.length; i++) {
    if (i < period - 1) { runSum += closes[i]; prev = runSum / (i + 1); }
    else if (i === period - 1) { runSum += closes[i]; prev = runSum / period; }
    else prev = closes[i] * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

/** Raw Bhairav state machine (same rules as computeBhairavOverlay). */
function rawSteps(candles: CandleRow[], candleType: 'CLH' | 'HA'): Array<{ signal: BhairavAction | null }> {
  const t = transformCandles(candles, candleType);
  const bodyHigh = (i: number) => Math.max(Number(t[i].open), Number(t[i].close));
  const bodyLow = (i: number) => Math.min(Number(t[i].open), Number(t[i].close));
  const out: Array<{ signal: BhairavAction | null }> = [];
  let pos: 'LONG' | 'SHORT' | 'FLAT' = 'FLAT';
  for (let i = 0; i < t.length; i++) {
    let signal: BhairavAction | null = null;
    if (i >= 1) {
      const hiUp = bodyHigh(i) > bodyHigh(i - 1);
      const hiDn = bodyHigh(i) <= bodyHigh(i - 1);
      const loDn = bodyLow(i) < bodyLow(i - 1);
      const loUp = bodyLow(i) >= bodyLow(i - 1);
      if (pos === 'LONG') {
        if (loDn) { signal = 'SHORT'; pos = 'SHORT'; }
        else if (hiDn) { signal = 'SELL'; pos = 'FLAT'; }
      } else if (pos === 'SHORT') {
        if (hiUp) { signal = 'BUY'; pos = 'LONG'; }
        else if (loUp) { signal = 'COVER'; pos = 'FLAT'; }
      } else {
        if (hiUp && loDn) { const up = Number(t[i].close) > Number(t[i].open); signal = up ? 'BUY' : 'SHORT'; pos = up ? 'LONG' : 'SHORT'; }
        else if (hiUp) { signal = 'BUY'; pos = 'LONG'; }
        else if (loDn) { signal = 'SHORT'; pos = 'SHORT'; }
      }
    }
    out.push({ signal });
  }
  return out;
}

/** Bhairav CLH position per bar: +1 LONG / -1 SHORT / 0 FLAT. */
function posSeries(bars: CandleRow[]): number[] {
  const steps = rawSteps(bars, 'CLH');
  const out = new Array<number>(steps.length).fill(0);
  let pos = 0;
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i].signal;
    if (s === 'BUY') pos = 1;
    else if (s === 'SHORT') pos = -1;
    else if (s === 'SELL' || s === 'COVER') pos = 0;
    out[i] = pos;
  }
  return out;
}

function weeklyFromDaily(daily: CandleRow[]): CandleRow[] {
  const weekly: CandleRow[] = [];
  for (const b of daily) {
    const wk = Math.floor(((b.time as number) - MON_ANCHOR) / WEEK) * WEEK + MON_ANCHOR;
    const cur = weekly[weekly.length - 1];
    if (!cur || cur.time !== wk) weekly.push({ time: wk, open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume || 0 });
    else {
      cur.high = Math.max(Number(cur.high), Number(b.high));
      cur.low = Math.min(Number(cur.low), Number(b.low));
      cur.close = b.close;
      cur.volume = (Number(cur.volume) || 0) + (Number(b.volume) || 0);
    }
  }
  return weekly;
}

function asOfIdx(tfBars: CandleRow[], tfMinutes: number, htfBars: CandleRow[], htfDurSec: number): number[] {
  const out = new Array<number>(tfBars.length).fill(-1);
  let j = -1;
  for (let i = 0; i < tfBars.length; i++) {
    const decision = (tfBars[i].time as number) + tfMinutes * 60;
    while (j + 1 < htfBars.length && (htfBars[j + 1].time as number) + htfDurSec <= decision) j++;
    out[i] = j;
  }
  return out;
}

export function computeGatedBhairavSteps(
  raw: CandleRow[], candleType: 'CLH' | 'HA', tfMinutes: number, dailyRaw?: CandleRow[],
): GatedBhairavResult {
  const profile = bhairavGateProfile(tfMinutes);
  const steps0 = rawSteps(raw, candleType);
  const n = steps0.length;

  let allow: (i: number, side: 1 | -1) => boolean;
  let htfOk = true;
  if (profile === 'WEEKLY') {
    allow = () => true;
  } else if (profile === 'DAILY') {
    const closes = raw.map(c => Number(c.close));
    const ema = emaSeries(closes, 50);
    const cloudByTime = new Map<number, number>();
    for (const p of computeCloudSeries(raw, [], [])) cloudByTime.set(p.time as number, p.cloudState);
    const cloud = raw.map(c => cloudByTime.get(c.time as number) ?? 0);
    allow = (i, side) => side === 1
      ? cloud[i] === 1 && closes[i] > ema[i]
      : cloud[i] === -1 && closes[i] < ema[i];
  } else if (profile === 'H4') {
    const daily = (dailyRaw || []).slice().sort((a, b) => (a.time as number) - (b.time as number));
    const weekly = weeklyFromDaily(daily);
    const dPos = daily.length >= 30 ? posSeries(daily) : [];
    const wPos = weekly.length >= 30 ? posSeries(weekly) : [];
    htfOk = dPos.length > 0 && wPos.length > 0;
    const dIdx = asOfIdx(raw, tfMinutes, daily, DAY);
    const wIdx = asOfIdx(raw, tfMinutes, weekly, WEEK);
    allow = (i, side) => {
      const d = dIdx[i] >= 0 && dPos.length ? dPos[dIdx[i]] : 0;
      const w = wIdx[i] >= 0 && wPos.length ? wPos[wIdx[i]] : 0;
      return d === side && w === side;
    };
  } else {
    allow = () => false;
  }

  const steps: GatedBhairavStep[] = [];
  let pos: 'LONG' | 'SHORT' | 'FLAT' = 'FLAT';
  for (let i = 0; i < n; i++) {
    const s = steps0[i].signal;
    let signal: BhairavAction | null = null;
    let reversed = false;
    let blockedSignal: BhairavAction | null = null;
    if (s) {
      const mayEnter = (side: 1 | -1) => i >= ENTRY_WARMUP && allow(i, side);
      if (pos === 'LONG' && (s === 'SELL' || s === 'SHORT')) {
        if (s === 'SHORT' && mayEnter(-1)) { signal = 'SHORT'; reversed = true; pos = 'SHORT'; }
        else { signal = 'SELL'; pos = 'FLAT'; if (s === 'SHORT') blockedSignal = 'SHORT'; }
      } else if (pos === 'SHORT' && (s === 'COVER' || s === 'BUY')) {
        if (s === 'BUY' && mayEnter(1)) { signal = 'BUY'; reversed = true; pos = 'LONG'; }
        else { signal = 'COVER'; pos = 'FLAT'; if (s === 'BUY') blockedSignal = 'BUY'; }
      } else if (pos === 'FLAT') {
        if (s === 'BUY') { if (mayEnter(1)) { signal = 'BUY'; pos = 'LONG'; } else blockedSignal = 'BUY'; }
        else if (s === 'SHORT') { if (mayEnter(-1)) { signal = 'SHORT'; pos = 'SHORT'; } else blockedSignal = 'SHORT'; }
      }
    }
    steps.push({ time: raw[i].time as number, pos, signal, reversed, blockedSignal });
  }
  return { steps, profile, gateLabel: bhairavGateLabel(profile), htfOk };
}
